import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'

import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { list_cabinet_invoices } from '@/api/cabinet'
import { WireInstructionsDialog } from '@/components/billing/wire-instructions-dialog'
import type { CabinetInvoice } from '@/types/subscription'
import { FileText, Download, Receipt } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'

export default function CabinetBilling() {
  const { t } = useTranslation()
  const [invoices, setInvoices] = useState<CabinetInvoice[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [instructionsOpen, setInstructionsOpen] = useState<boolean>(false)

  const loadInvoices = async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await list_cabinet_invoices()
      setInvoices(Array.isArray(resp) ? resp : [])
    } catch (e: any) {
      setError(e?.message || 'Failed to load invoices')
      setInvoices([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInvoices()
  }, [])

  const formatMoney = (cents?: number | null, currency?: string | null) => {
    if (cents == null) return '-'
    const amount = (cents / 100).toFixed(2)
    return currency ? `${amount} ${currency}` : amount
  }

  const formatDate = (isoOrEpoch?: string | number | null) => {
    if (isoOrEpoch == null) return '-'
    try {
      if (typeof isoOrEpoch === 'number') {
        const d = new Date(isoOrEpoch * 1000)
        return d.toLocaleDateString()
      }
      const d = new Date(isoOrEpoch)
      return d.toLocaleDateString()
    } catch {
      return String(isoOrEpoch)
    }
  }

  // An issued invoice is only settled once its payment succeeded
  const statusBadge = (inv: CabinetInvoice) => {
    const common = 'text-xs'
    if (inv.status === 'void') return <Badge variant="outline" className={common}>{t('void') || 'Void'}</Badge>
    const ps = inv.payment_status
    if (ps === 'succeeded' || inv.status === 'paid') return <Badge className={`${common} bg-green-600 hover:bg-green-600 text-white`}>{t('paid') || 'Paid'}</Badge>
    if (ps === 'failed') return <Badge variant="destructive" className={common}>{t('failed') || 'Failed'}</Badge>
    return <Badge className={`${common} bg-amber-500 hover:bg-amber-500 text-white`}>{t('awaiting_payment') || 'Awaiting payment'}</Badge>
  }

  const sanitizeUrl = (url?: string | null) => {
    if (!url) return null
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    return null
  }

  return (
    <>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Receipt className="h-5 w-5" />
            </span>
          }
          title={t('invoices') || 'Invoices'}
          description={t('cabinet_billing_desc') || 'View and download your cabinet billing invoices.'}
        />

        <Card>
          <CardContent className='p-4'>
            {loading ? (
              <div className='py-8 text-center'>{t('loading') || 'Loading'}...</div>
            ) : error ? (
              <div className='py-8 text-center text-red-600'>{error}</div>
            ) : invoices.length === 0 ? (
              <div className='py-12 text-center text-muted-foreground'>
                <FileText className='mx-auto h-10 w-10 mb-2' />
                {t('no_invoices') || 'No invoices available yet.'}
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='whitespace-nowrap'>{t('invoice_number') || 'Invoice #'}</TableHead>
                      <TableHead className='whitespace-nowrap'>{t('issue_date') || 'Issue Date'}</TableHead>
                      <TableHead className='whitespace-nowrap'>{t('status') || 'Status'}</TableHead>
                      <TableHead className='whitespace-nowrap'>{t('total') || 'Total'}</TableHead>
                      <TableHead className='whitespace-nowrap text-right'>{t('actions') || 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => {
                      const url = sanitizeUrl(inv.pdf_url)
                      return (
                        <TableRow key={inv.id ?? inv.number}>
                          <TableCell className='font-mono text-sm'>{inv.number || '-'}</TableCell>
                          <TableCell>{formatDate(inv.issue_date)}</TableCell>
                          <TableCell>{statusBadge(inv)}</TableCell>
                          <TableCell>
                            {formatMoney(inv.total_cents, inv.currency)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {url ? (
                              <Button asChild variant='outline' size='sm'>
                                <a href={url} target='_blank' rel='noopener noreferrer'>
                                  <Download className='h-4 w-4 mr-1' />
                                  {t('download') || 'Download'}
                                </a>
                              </Button>
                            ) : inv.status !== 'void' && inv.payment_status === 'pending' && inv.payment_method === 'wire' ? (
                              <Button variant='outline' size='sm' onClick={() => setInstructionsOpen(true)}>
                                {t('view_payment_instructions') || 'Payment details'}
                              </Button>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </Main>
      <WireInstructionsDialog open={instructionsOpen} onOpenChange={setInstructionsOpen} />
    </>
  )
}